import { useEffect, useMemo, useRef, useState } from "react";
import { ReportBackButton } from "../ledgerReports/ReportBackButton";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Chip, CircularProgress } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import moment from "moment";
import {
  globalStyle,
  handleVisible,
  handleDangerVisible,
} from "../../global/Utils";
import { SearchableSelect } from "../Common/SearchableSelect";
import { getUserList } from "../../Actions/UserAction";
import {
  getBuyer,
  getBuyerLedger,
  clearBuyerLedger,
  exportBuyerLedger,
} from "../../Actions/BuyerAction";
import {
  buildBuyerLedger,
  formatMoney,
  formatRate,
} from "../../global/buyerLedger";

/**
 * The ledger of one buyer over a window of time: every invoice billed to them,
 * its value, sales tax and invoice total, and the Section 236G and GSection 236H
 * withholding worked out on top - with the period's totals for each.
 *
 * Admin and customer read it the way they read the stock ledger: a customer
 * sees their own buyers only, while an admin picks a user first and the buyer
 * dropdown follows that choice. The server enforces the same rule, so the user
 * dropdown is the admin's convenience, not the boundary.
 *
 * Opened as /main/buyer-ledger?id=<buyerId> (the Buyers list links here), it
 * starts on that buyer.
 */

const today = () => moment().format("YYYY-MM-DD");

// A month back is the window most people want first.
const monthAgo = () => moment().subtract(1, "month").format("YYYY-MM-DD");

// Pakistan's tax year runs July to June, and 236G / 236H are withheld against
// it, so it gets a button of its own.
const fiscalYearStart = () => {
  const now = moment();
  const year = now.month() >= 6 ? now.year() : now.year() - 1;
  return `${year}-07-01`;
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Valid", label: "Valid" },
  { value: "Invalid", label: "Invalid" },
  { value: "Cancel", label: "Cancelled" },
];

const ENVIRONMENT_OPTIONS = [
  { value: "", label: "All Environments" },
  { value: "Production", label: "Production" },
  { value: "Sandbox", label: "Sandbox" },
];

const STATUS_CHIP = {
  Valid: "success",
  Invalid: "error",
  Cancelled: "warning",
};

const labelOf = (options, value) =>
  options.find((option) => option.value === value)?.label || "";

const Figure = ({ label, value, tone, money = true }) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      borderRadius: 2,
      border: "1px solid #E0E0E0",
      backgroundColor: "#FAFAFA",
      minWidth: 140,
      flex: "1 1 140px",
    }}
  >
    <Box sx={{ fontSize: 12, color: "#666" }}>{label}</Box>
    <Box
      sx={{
        fontSize: 18,
        fontWeight: 700,
        color: tone || globalStyle.bgColorBlue,
        whiteSpace: "nowrap",
      }}
    >
      {money ? formatMoney(value) : value}
    </Box>
  </Box>
);

// A withholding figure with its rate under it, or a dash when there is none.
const WithholdingCell = ({ amount, rate }) =>
  rate > 0 ? (
    <>
      {formatMoney(amount)}
      <div className="text-xs text-gray-500">@ {formatRate(rate)}</div>
    </>
  ) : (
    <span className="text-gray-400">-</span>
  );

const BuyerLedger = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const initialBuyerId = searchParams.get("id") || "";

  const { buyerList, isGettingBuyer } = useSelector(
    (state) => state.BuyerReducer,
  );
  const {
    buyerLedgerInvoices,
    buyerLedgerBuyer,
    isGettingBuyerLedger,
    buyerLedgerError,
    isExportingBuyerLedger,
  } = useSelector((state) => state.BuyerReducer);
  const { userList, isGettingUserList } = useSelector(
    (state) => state.UserReducer,
  );
  const { userDetails } = useSelector((state) => state?.HeaderReducer);
  const isAdmin = userDetails?.role === "admin";

  const [userId, setUserId] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [status, setStatus] = useState("");
  const [environment, setEnvironment] = useState("");
  const [dateError, setDateError] = useState("");
  const appliedInitialBuyer = useRef(false);

  useEffect(() => {
    // Empty page/limit means "the whole list" (listPaging in Utils.js) - the
    // dropdown filters in the browser.
    dispatch(getBuyer("", "", ""));
  }, [dispatch]);

  useEffect(() => {
    // Only an admin can choose whose ledger to read, so only an admin needs
    // the list of users to choose from.
    if (isAdmin) {
      dispatch(getUserList("", "", "", ""));
    }
  }, [dispatch, isAdmin]);

  // Opened from a buyer's row: start on that buyer (and, for an admin, on the
  // user it belongs to) once the buyer list has arrived.
  useEffect(() => {
    if (appliedInitialBuyer.current || !initialBuyerId) return;
    const buyer = (buyerList || []).find((b) => b._id === initialBuyerId);
    if (!buyer) return;
    appliedInitialBuyer.current = true;
    if (isAdmin) setUserId(buyer?.createdBy?._id || "");
    setBuyerId(buyer._id);
  }, [buyerList, initialBuyerId, isAdmin]);

  /**
   * A customer's buyer list already comes back scoped to them, so there is
   * nothing further to narrow; an admin's holds every user's buyers, and is
   * narrowed to the user they picked.
   */
  const buyers = useMemo(
    () =>
      (buyerList || []).filter(
        (buyer) => !isAdmin || buyer?.createdBy?._id === userId,
      ),
    [buyerList, isAdmin, userId],
  );

  const filters = useMemo(
    () => ({
      startDate,
      endDate,
      FBRInvoiceStatus: status,
      companyStatus: environment,
    }),
    [startDate, endDate, status, environment],
  );

  useEffect(() => {
    if (!buyerId) {
      dispatch(clearBuyerLedger());
      return;
    }
    if (startDate && endDate && startDate > endDate) {
      return;
    }
    dispatch(getBuyerLedger(buyerId, filters));
  }, [dispatch, buyerId, filters, startDate, endDate]);

  useEffect(() => {
    setDateError(
      startDate && endDate && startDate > endDate
        ? "The start date cannot be after the end date."
        : "",
    );
  }, [startDate, endDate]);

  const ledger = useMemo(
    () => buildBuyerLedger(buyerLedgerInvoices),
    [buyerLedgerInvoices],
  );
  const { rows, totals, invoiceCount, cancelledCount } = ledger;

  const handleUserChange = (value) => {
    setUserId(value);
    // The buyer belonged to the user just switched away from, so it goes with
    // them rather than leaving a ledger on screen under the wrong name.
    setBuyerId("");
  };

  const printLedger = (type) => {
    dispatch(
      exportBuyerLedger(
        buyerId,
        filters,
        type,
        {
          buyer: buyerLedgerBuyer,
          userEmail: isAdmin
            ? userList?.find((user) => user._id === userId)?.email || ""
            : userDetails?.email || "",
          startDate,
          endDate,
          statusLabel: status ? labelOf(STATUS_OPTIONS, status) : "",
          environmentLabel: environment
            ? labelOf(ENVIRONMENT_OPTIONS, environment)
            : "",
        },
        handleVisible,
        handleDangerVisible,
      ),
    );
  };

  const setRange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <Box className="p-4">
      <ReportBackButton />
      <Box sx={{ mb: 2 }}>
        <Box
          component="h1"
          sx={{
            fontFamily: globalStyle.fontFamilyHeading,
            fontWeight: 700,
            color: globalStyle.bgColorBlue,
            fontSize: {
              xs: globalStyle.headingFontSizeSmall,
              sm: globalStyle.headingFontSize,
            },
            lineHeight: 1.2,
          }}
        >
          Buyer Ledger
        </Box>
        <Box
          component="p"
          sx={{
            mt: 0.5,
            fontFamily: globalStyle.fontFamilyParagraph,
            fontSize: { xs: 12, sm: 14 },
            color: "#666",
          }}
        >
          Every invoice billed to one buyer over a period, with its sales tax,
          invoice total and the Section 236G and GSection 236H withheld on it.
        </Box>
      </Box>

      {/* ---- filters ---- */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        {isAdmin &&
          (isGettingUserList ? (
            <div className="flex justify-center items-center h-20">
              <CircularProgress size={26} />
            </div>
          ) : (
            <div className="mb-4">
              <label className="block mb-1 font-medium">
                Select User<span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="userId"
                value={userId}
                onChange={(e) => handleUserChange(e.target.value)}
                disabled={!userList || userList.length === 0}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
              >
                {!userList || userList.length === 0 ? (
                  <option value="">No Users Available</option>
                ) : (
                  <>
                    <option value="">SELECT</option>
                    {userList.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.email}
                      </option>
                    ))}
                  </>
                )}
              </SearchableSelect>
            </div>
          ))}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block mb-1 font-medium">Buyer</label>
            <SearchableSelect
              name="buyerId"
              value={buyerId}
              onChange={(e) => setBuyerId(e.target.value)}
              // An admin has chosen nobody yet, so there is nothing to list.
              disabled={(isAdmin && !userId) || isGettingBuyer}
              className="uppercase w-full px-4 py-2 border border-gray-300 rounded-md"
            >
              <option value="">
                {isAdmin && !userId
                  ? "Select a user first"
                  : isGettingBuyer
                    ? "Loading buyers..."
                    : "Select a buyer"}
              </option>
              {buyers.map((buyer) => (
                <option key={buyer._id} value={buyer._id}>
                  {buyer.businessName}
                  {buyer.registrationNo ? ` — ${buyer.registrationNo}` : ""}
                </option>
              ))}
            </SearchableSelect>
            {isAdmin && userId && !isGettingBuyer && buyers.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">
                This user has no buyers.
              </p>
            )}
          </div>

          <div>
            <label className="block mb-1 font-medium">Start Date</label>
            <input
              type="date"
              value={startDate}
              max={endDate || today()}
              onChange={(e) => setStartDate(e.target.value)}
              className={`w-full px-4 py-2 border rounded-md ${
                dateError ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">End Date</label>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              className={`w-full px-4 py-2 border rounded-md ${
                dateError ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block mb-1 font-medium">FBR Invoice Status</label>
            <SearchableSelect
              name="FBRInvoiceStatus"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SearchableSelect>
          </div>

          <div className="md:col-span-2">
            <label className="block mb-1 font-medium">Environment</label>
            <SearchableSelect
              name="companyStatus"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
            >
              {ENVIRONMENT_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SearchableSelect>
          </div>
        </div>

        {dateError && (
          <p className="text-red-500 text-sm mt-2">{dateError}</p>
        )}

        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" onClick={() => setRange(monthAgo(), today())}>
            Last month
          </Button>
          <Button
            size="small"
            onClick={() => setRange(fiscalYearStart(), today())}
          >
            This tax year
          </Button>
          <Button size="small" onClick={() => setRange("", "")}>
            All time
          </Button>
        </Box>
      </Box>

      {/* ---- the ledger ---- */}
      {isAdmin && !userId ? (
        <Box py={6} textAlign="center" color="text.secondary">
          Select a user, then one of their buyers, to see the buyer's ledger.
        </Box>
      ) : !buyerId ? (
        <Box py={6} textAlign="center" color="text.secondary">
          Select a buyer to see their ledger.
        </Box>
      ) : isGettingBuyerLedger ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={34} />
        </Box>
      ) : buyerLedgerError ? (
        <Box py={4} textAlign="center" color="error.main">
          {buyerLedgerError}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Box
                  sx={{
                    fontFamily: globalStyle.fontFamilyHeading,
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  {buyerLedgerBuyer?.businessName}
                </Box>
                {buyerLedgerBuyer?.registrationType && (
                  <Chip
                    label={buyerLedgerBuyer.registrationType}
                    size="small"
                    color={
                      String(buyerLedgerBuyer.registrationType).toLowerCase() ===
                      "registered"
                        ? "success"
                        : "default"
                    }
                  />
                )}
              </Box>
              {buyerLedgerBuyer?.registrationNo && (
                <Box sx={{ fontSize: 13, color: "#666" }}>
                  NTN/CNIC: {buyerLedgerBuyer.registrationNo}
                </Box>
              )}
            </Box>

            {/* The ledger exists now, so there is something to print. */}
            {rows.length > 0 && (
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<PictureAsPdfIcon />}
                  disabled={isExportingBuyerLedger}
                  onClick={() => printLedger("PDF")}
                >
                  PDF
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="success"
                  startIcon={<GridOnIcon />}
                  disabled={isExportingBuyerLedger}
                  onClick={() => printLedger("EXCEL")}
                >
                  Excel
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Figure label="Invoices" value={invoiceCount} money={false} />
            <Figure label="Sales Tax" value={totals.salesTax} />
            <Figure label="Invoice Total" value={totals.invoiceTotal} />
            <Figure label="Section 236G" value={totals.section236G} />
            <Figure label="GSection 236H" value={totals.section236H} />
            <Figure
              label="Total Receivable"
              value={totals.totalReceivable}
              tone="#1E7D34"
            />
          </Box>

          {rows.length === 0 ? (
            <Box py={5} textAlign="center" color="text.secondary">
              No invoices for this buyer in this period. Widen the dates or
              clear the filters to see rows here.
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr
                      style={{
                        background: globalStyle.blueColor,
                        color: "#fff",
                      }}
                    >
                      <th className="text-center px-3 py-2">Sr.</th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Invoice No.</th>
                      <th className="text-left px-3 py-2">FBR Invoice Ref No.</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2">Value Excl. ST</th>
                      <th className="text-right px-3 py-2">Sales Tax</th>
                      <th className="text-right px-3 py-2">Invoice Total</th>
                      <th className="text-right px-3 py-2">Section 236G</th>
                      <th className="text-right px-3 py-2">GSection 236H</th>
                      <th className="text-right px-3 py-2">Total Receivable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.id || row.sr}
                        className="border-b"
                        style={
                          row.cancelled
                            ? { color: "#9E9E9E", fontStyle: "italic" }
                            : undefined
                        }
                      >
                        <td className="px-3 py-2 text-center">{row.sr}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.date
                            ? moment(row.date).format("DD-MM-YYYY")
                            : "-"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.invoiceNumber || "-"}
                          {row.invoiceType &&
                            row.invoiceType !== "Sale Invoice" && (
                              <div className="text-xs text-gray-500">
                                {row.invoiceType}
                              </div>
                            )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.FBRInvoiceRefNo || "-"}
                        </td>
                        <td className="px-3 py-2">
                          <Chip
                            label={row.status}
                            size="small"
                            color={STATUS_CHIP[row.status] || "default"}
                          />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {formatMoney(row.valueExclST)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {formatMoney(row.salesTax)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {formatMoney(row.invoiceTotal)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <WithholdingCell
                            amount={row.section236G}
                            rate={row.rate236G}
                          />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <WithholdingCell
                            amount={row.section236H}
                            rate={row.rate236H}
                          />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap font-bold">
                          {formatMoney(row.totalReceivable)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr
                      className="font-bold"
                      style={{ backgroundColor: "#F4F6FA" }}
                    >
                      <td className="px-3 py-2 text-center" colSpan={5}>
                        TOTAL ({invoiceCount} invoice
                        {invoiceCount === 1 ? "" : "s"})
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatMoney(totals.valueExclST)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatMoney(totals.salesTax)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatMoney(totals.invoiceTotal)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatMoney(totals.section236G)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatMoney(totals.section236H)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatMoney(totals.totalReceivable)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {cancelledCount > 0 && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    fontSize: 13,
                    color: "#666",
                    borderTop: "1px solid #EEE",
                  }}
                >
                  {cancelledCount} cancelled invoice
                  {cancelledCount === 1 ? " is" : "s are"} listed for the
                  record but not included in the totals.
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default BuyerLedger;
